import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, Switch, Checkbox,
  Radio, RadioGroup, FormControlLabel, Paper,
  Select, MenuItem, TextField
} from '@mui/material';
import { SketchPicker } from 'react-color';

const PLANETS = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu', 'Ketu'];
const ASPECTS = [0, 30, 60, 90, 120, 150, 180];
const OTHER_ASPECTS = [30, 60, 90, 120, 150, 180]; // indices 1-6

const LEGEND_ITEMS = [
  { key: 'declinationAspects', label: 'Declination Aspects' },
  { key: 'signIngress',        label: 'Sign Ingress'        },
  { key: 'nakshatraIngress',   label: 'Nakshatra Ingress'   },
  { key: 'distance',           label: 'Distance'            },
  { key: 'combust',            label: 'Combust'             },
  { key: 'retro',              label: 'Retro'               },
  { key: 'h1',                 label: 'H1'                  },
  { key: 'g1',                 label: 'G1'                  },
];

const allPlus = () => Object.fromEntries(PLANETS.map(p => [p, '+']));

// Classic Bradley: no Moon/Rahu/Ketu; Ptolemy 5 aspects (0,60,90,120,180); no declination
const CLASSIC_EN = Object.fromEntries(PLANETS.map(p => [p, !['Moon','Rahu','Ketu'].includes(p)]));
const CLASSIC_ASP = Object.fromEntries(PLANETS.map(p => [
  p, ASPECTS.map((a, i) => !['Moon','Rahu','Ketu'].includes(p) && [0,2,3,4,6].includes(i))
]));
const CLASSIC_POL = { Sun:'+', Mercury:'+', Venus:'+', Mars:'-', Jupiter:'+', Saturn:'-', Uranus:'+', Neptune:'-', Pluto:'-', Moon:'+', Rahu:'+', Ketu:'+' };

// Default aspect polarities (non-conjunction): traditional Bradley assignments
const DEFAULT_ASPECT_POLARITY = { 30: '+', 60: '+', 90: '-', 120: '+', 150: '+', 180: '-' };

const DEFAULT_PLANET_WEIGHTS = Object.fromEntries(PLANETS.map(p => [p, 1]));
const DEFAULT_ASPECT_WEIGHTS = Object.fromEntries(ASPECTS.map(a => [a, 1]));

export const PRESET_CLASSIC = {
  planetEnabled: CLASSIC_EN,
  planetAspects: CLASSIC_ASP,
  conjPolarity:  CLASSIC_POL,
  aspectPolarity: { ...DEFAULT_ASPECT_POLARITY },
  planetWeights: { ...DEFAULT_PLANET_WEIGHTS },
  aspectWeights: { ...DEFAULT_ASPECT_WEIGHTS },
  planetDecl:    Object.fromEntries(PLANETS.map(p => [p, ['Venus','Mars'].includes(p)])),
  colHeaders:    ASPECTS.map((_, i) => [0,2,3,4,6].includes(i)),
  declHeader:    false,
  legend:        Object.fromEntries(LEGEND_ITEMS.map(i => [i.key, false])),
  longWeight:    5,
  mediumWeight:  1,
  classicSmoothingDays: null,
};
export const PRESET_ALL = {
  planetEnabled: Object.fromEntries(PLANETS.map(p => [p, true])),
  planetAspects: Object.fromEntries(PLANETS.map(p => [p, ASPECTS.map(() => true)])),
  conjPolarity:  allPlus(),
  aspectPolarity: { ...DEFAULT_ASPECT_POLARITY },
  planetWeights: { ...DEFAULT_PLANET_WEIGHTS },
  aspectWeights: { ...DEFAULT_ASPECT_WEIGHTS },
  planetDecl:    Object.fromEntries(PLANETS.map(p => [p, true])),
  colHeaders:    ASPECTS.map(() => true),
  longWeight:    5,
  mediumWeight:  1,
  classicSmoothingDays: null,
  declHeader:    true,
  legend:        Object.fromEntries(LEGEND_ITEMS.map(i => [i.key, true])),
};
const VEDIC_PLUS_EN = Object.fromEntries(PLANETS.map(p => [p, p !== 'Moon']));
const VEDIC_PLUS_ASP = Object.fromEntries(PLANETS.map(p => [
  p, ASPECTS.map(() => p !== 'Moon')
]));
const VEDIC_PLUS_POL = Object.fromEntries(PLANETS.map(p => [p, '+']));
export const PRESET_VEDIC_PLUS = {
  planetEnabled: VEDIC_PLUS_EN,
  planetAspects: VEDIC_PLUS_ASP,
  conjPolarity:  VEDIC_PLUS_POL,
  aspectPolarity: { ...DEFAULT_ASPECT_POLARITY },
  planetWeights: { ...DEFAULT_PLANET_WEIGHTS },
  aspectWeights: { ...DEFAULT_ASPECT_WEIGHTS },
  planetDecl:    Object.fromEntries(PLANETS.map(p => [p, false])),
  colHeaders:    ASPECTS.map(() => true),
  declHeader:    false,
  legend:        Object.fromEntries(LEGEND_ITEMS.map(i => [i.key, true])),
  longWeight:    5,
  mediumWeight:  1,
  classicSmoothingDays: null,
  lineColor:     'rgb(190, 15, 225)',
};
// The Custom preset's weights, given as figures rather than derived from anything: this is a
// hand-tuned configuration, and the numbers are the specification.
//
// Moon carries a weight like every other body even though it is switched OFF and holds no aspects
// in this preset. The weight is what the body WOULD contribute; whether it contributes at all is
// planetEnabled's job. Zeroing it here would make turning the Moon back on a silent no-op.
const CUSTOM_PLANET_WEIGHTS = { Sun:2, Moon:1, Mercury:2, Venus:1, Mars:1, Jupiter:2, Saturn:2, Uranus:2, Neptune:2, Pluto:2, Rahu:3, Ketu:3 };

// The per-column figures under the aspect matrix. The trine is the heaviest by a distance (6 against
// 3 for the conjunction, square and opposition), and the minor aspects - 30, 60, 150 - are 1.
const CUSTOM_ASPECT_WEIGHTS = { 0:3, 30:1, 60:1, 90:3, 120:6, 150:1, 180:3 };
const ALL_POSITIVE_ASPECT_POLARITY = { 30: '+', 60: '+', 90: '+', 120: '+', 150: '+', 180: '+' };
export const PRESET_CUSTOM = {
  ...PRESET_ALL,
  planetEnabled: Object.fromEntries(PLANETS.map(p => [p, p !== 'Moon'])),
  planetAspects: Object.fromEntries(PLANETS.map(p => [p, ASPECTS.map(() => p !== 'Moon')])),
  conjPolarity:  allPlus(),
  aspectPolarity: { ...ALL_POSITIVE_ASPECT_POLARITY },
  planetWeights: { ...CUSTOM_PLANET_WEIGHTS },
  aspectWeights: { ...CUSTOM_ASPECT_WEIGHTS },
  planetDecl:    Object.fromEntries(PLANETS.map(p => [p, false])),
  declHeader:    false,
  legend:        Object.fromEntries(LEGEND_ITEMS.map(i => [i.key, i.key !== 'distance'])),
  longWeight:    5,
  mediumWeight:  1,
  classicSmoothingDays: null,
};

/**
 * The dialog's own defaults for the things no preset carries.
 *
 * <p>Hoisted out of the useState calls below so the toolbar's direct run and the dialog cannot
 * drift: the toolbar now runs TrendPulse WITHOUT opening this dialog, and if it carried its own
 * copy of these figures the two would silently diverge the first time either was edited.
 */
export const TRENDPULSE_LINE_DEFAULTS = {
  lineType: 'solid',
  lineThickness: 2,
  lineColor: 'rgb(190, 15, 225)',
  toleranceDays: 5,
};

/**
 * Everything {@code handleAstroGraphExecute} needs to run the Custom preset with no dialog open.
 *
 * <p>A function rather than a constant, and the nested objects are copied: the caller passes this
 * straight into React state, and handing out the module's own preset object would let one run's
 * edits reach the next one's defaults.
 */
export function trendPulseCustomRunConfig() {
  return {
    ...PRESET_CUSTOM,
    planetEnabled: { ...PRESET_CUSTOM.planetEnabled },
    planetAspects: Object.fromEntries(
      Object.entries(PRESET_CUSTOM.planetAspects).map(([p, row]) => [p, [...row]])),
    conjPolarity: { ...PRESET_CUSTOM.conjPolarity },
    aspectPolarity: { ...PRESET_CUSTOM.aspectPolarity },
    planetWeights: { ...PRESET_CUSTOM.planetWeights },
    aspectWeights: { ...PRESET_CUSTOM.aspectWeights },
    planetDecl: { ...PRESET_CUSTOM.planetDecl },
    legend: { ...PRESET_CUSTOM.legend },
    preset: 'custom',
    ...TRENDPULSE_LINE_DEFAULTS,
  };
}

/* ── small reusable components ── */
const GreenCb = ({ checked, onChange, disabled }) => (
  <Checkbox checked={checked} onChange={e => onChange(e.target.checked)} disabled={disabled}
    sx={{ p: 0.5, color: '#555', '&.Mui-checked': { color: '#2e7d32' }, '& .MuiSvgIcon-root': { fontSize: 24 } }} />
);
GreenCb.propTypes = { checked: PropTypes.bool.isRequired, onChange: PropTypes.func.isRequired, disabled: PropTypes.bool };

const PolarityBtn = ({ value, onChange, disabled }) => (
  <Button size="small" variant="outlined" onClick={() => !disabled && onChange(value === '+' ? '-' : '+')}
    disabled={disabled}
    sx={{ minWidth: 28, height: 26, px: 0.5, py: 0, fontSize: 14, fontWeight: 700, lineHeight: 1,
      color: value === '+' ? '#2e7d32' : '#c62828',
      borderColor: value === '+' ? '#2e7d32' : '#c62828',
      '&:hover': { borderColor: value === '+' ? '#1b5e20' : '#b71c1c', backgroundColor: 'transparent' },
    }}>
    {value}
  </Button>
);
PolarityBtn.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, disabled: PropTypes.bool };

const WeightBox = ({ value, onChange }) => (
  <TextField size="small" value={value}
    onChange={e => {
      const v = e.target.value;
      if (v === '' || v === '-' || /^-?\d*\.?\d*$/.test(v)) onChange(v);
    }}
    onBlur={e => {
      const n = parseFloat(e.target.value);
      onChange(Number.isFinite(n) ? n : 1);
    }}
    inputProps={{ style: { textAlign: 'center', padding: '2px 0', fontSize: 12 } }}
    sx={{ width: 38, '& .MuiOutlinedInput-root': { height: 24 } }}
  />
);
WeightBox.propTypes = { value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired, onChange: PropTypes.func.isRequired };

const CELL_W = 50;
const ROW_H  = 36;

const panelBorder = { border: '1px solid #bbb', borderRadius: 1, p: 1 };

/* ── Panel header helper ── */
const ColLabel = ({ label }) => (
  <Box sx={{ width: CELL_W, display: 'flex', justifyContent: 'center' }}>
    <Typography fontWeight={600} fontSize={14}>{label}</Typography>
  </Box>
);
ColLabel.propTypes = { label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired };

const AstroGraphDialog = ({ open, onClose, onExecute, onExecuteAndStudy, onAnimate, commentary, barData }) => {
  const [planetEnabled, setPlanetEnabled] = useState(PRESET_CLASSIC.planetEnabled);
  const [planetAspects, setPlanetAspects] = useState(PRESET_CLASSIC.planetAspects);
  const [conjPolarity,  setConjPolarity]  = useState(PRESET_CLASSIC.conjPolarity);
  const [aspectPolarity, setAspectPolarity] = useState(PRESET_CLASSIC.aspectPolarity);
  const [planetWeights, setPlanetWeights] = useState(PRESET_CLASSIC.planetWeights);
  const [aspectWeights, setAspectWeights] = useState(PRESET_CLASSIC.aspectWeights);
  const [planetDecl,    setPlanetDecl]    = useState(PRESET_CLASSIC.planetDecl);
  const [colHeaders,    setColHeaders]    = useState(PRESET_CLASSIC.colHeaders);
  const [declHeader,    setDeclHeader]    = useState(PRESET_CLASSIC.declHeader);
  const [legend,        setLegend]        = useState(PRESET_CLASSIC.legend);
  const [preset,        setPreset]        = useState('classic');
  const [longWeight,    setLongWeight]    = useState(PRESET_CLASSIC.longWeight);
  const [mediumWeight,  setMediumWeight]  = useState(PRESET_CLASSIC.mediumWeight);
  const [classicSmoothingDays, setClassicSmoothingDays] = useState(PRESET_CLASSIC.classicSmoothingDays);
  const [lineType,      setLineType]      = useState(TRENDPULSE_LINE_DEFAULTS.lineType);
  const [lineThickness, setLineThickness] = useState(TRENDPULSE_LINE_DEFAULTS.lineThickness);
  const [lineColor,     setLineColor]     = useState(TRENDPULSE_LINE_DEFAULTS.lineColor);
  const [toleranceDays, setToleranceDays] = useState(TRENDPULSE_LINE_DEFAULTS.toleranceDays);
  const [studyStartDate, setStudyStartDate] = useState('2010-10-10');
  const [animateDelay, setAnimateDelay] = useState('1');

  const { minStudyDate, maxStudyDate, defaultStudyDate } = useMemo(() => {
    if (!barData || !barData.dates || barData.dates.length < 120) {
      return { minStudyDate: '', maxStudyDate: '', defaultStudyDate: '' };
    }
    const min = barData.dates[100].substring(0, 10);
    const max = barData.dates[barData.dates.length - 21].substring(0, 10);
    return { minStudyDate: min, maxStudyDate: max, defaultStudyDate: max };
  }, [barData]);

  const goCustom = () => { if (preset !== 'custom') setPreset('custom'); };

  const applyPreset = (p) => {
    setPreset(p);
    const cfg = p === 'classic' ? PRESET_CLASSIC : p === 'all' ? PRESET_ALL : p === 'vedic_plus' ? PRESET_VEDIC_PLUS : PRESET_CUSTOM;
    setPlanetEnabled(cfg.planetEnabled);
    setPlanetAspects(cfg.planetAspects);
    setConjPolarity(cfg.conjPolarity);
    setAspectPolarity(cfg.aspectPolarity);
    setPlanetWeights(cfg.planetWeights);
    setAspectWeights(cfg.aspectWeights);
    setPlanetDecl(cfg.planetDecl);
    setColHeaders(cfg.colHeaders);
    setDeclHeader(cfg.declHeader);
    setLegend(cfg.legend);
    setLongWeight(cfg.longWeight);
    setMediumWeight(cfg.mediumWeight);
    setClassicSmoothingDays(cfg.classicSmoothingDays);
    if (cfg.lineColor) setLineColor(cfg.lineColor);
  };

  const toggleColHeader = (ai) => {
    goCustom();
    const next = !colHeaders[ai];
    setColHeaders(prev => prev.map((v, i) => i === ai ? next : v));
    setPlanetAspects(prev =>
      Object.fromEntries(Object.entries(prev).map(([pl, vals]) => [pl, vals.map((v, i) => i === ai ? next : v)]))
    );
  };

  const toggleDeclHeader = () => {
    goCustom();
    const next = !declHeader;
    setDeclHeader(next);
    setPlanetDecl(Object.fromEntries(PLANETS.map(p => [p, next])));
  };

  const togglePlanet = (planet) => {
    goCustom();
    const enabling = !planetEnabled[planet];
    setPlanetEnabled(prev => ({ ...prev, [planet]: enabling }));
    if (!enabling) {
      setPlanetAspects(prev => ({ ...prev, [planet]: ASPECTS.map(() => false) }));
      setPlanetDecl(prev => ({ ...prev, [planet]: false }));
    }
  };

  const toggleAspect = (planet, ai) => {
    goCustom();
    setPlanetAspects(prev => ({ ...prev, [planet]: prev[planet].map((v, i) => i === ai ? !v : v) }));
  };

  const toggleDecl = (planet) => {
    goCustom();
    setPlanetDecl(prev => ({ ...prev, [planet]: !prev[planet] }));
  };

  const toggleLegend = (key) => {
    goCustom();
    setLegend(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDeselect = () => {
    setPreset('custom');
    setPlanetAspects(Object.fromEntries(PLANETS.map(p => [p, ASPECTS.map(() => false)])));
    setPlanetDecl(Object.fromEntries(PLANETS.map(p => [p, false])));
    setColHeaders(ASPECTS.map(() => false));
    setDeclHeader(false);
    setLegend(Object.fromEntries(LEGEND_ITEMS.map(i => [i.key, false])));
  };

  const handleExecute = () => {
    if (onExecute) {
      onExecute({
        planetEnabled,
        planetAspects,
        conjPolarity,
        aspectPolarity,
        planetWeights,
        aspectWeights,
        planetDecl,
        legend,
        preset,
        longWeight,
        mediumWeight,
        classicSmoothingDays,
        lineType,
        lineThickness,
        lineColor,
        toleranceDays,
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl">
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, fontSize: 24, pb: 1 }}>
        TrendPulse
      </DialogTitle>

      <DialogContent sx={{ pt: 0, pb: 0 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>

          {/* ── PANEL 0: Planet weights ── */}
          <Box sx={{ ...panelBorder, pt: 1 }}>
            <Box sx={{ height: ROW_H * 3, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', pb: 0.5 }}>
              <Typography fontWeight={600} fontSize={11} color="text.secondary">Wt</Typography>
            </Box>
            {PLANETS.map(planet => (
              <Box key={planet} sx={{ height: ROW_H, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WeightBox
                  value={planetWeights[planet]}
                  onChange={v => { goCustom(); setPlanetWeights(prev => ({ ...prev, [planet]: v })); }}
                />
              </Box>
            ))}
          </Box>

          {/* ── PANEL 1: Planet names + switches ── */}
          <Box sx={{ ...panelBorder, pt: 1 }}>
            {/* header spacer to align with other panels' header rows */}
            <Box sx={{ height: ROW_H * 3, display: 'flex', alignItems: 'flex-end', pb: 0.5 }}>
              <Typography fontWeight={600} fontSize={13} color="text.secondary" sx={{ px: 0.5 }}>Planet</Typography>
            </Box>
            {PLANETS.map(planet => (
              <Box key={planet} sx={{ height: ROW_H, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Switch size="small" checked={planetEnabled[planet]} onChange={() => togglePlanet(planet)} />
                <Typography fontSize={14} sx={{ minWidth: 58 }}>{planet}</Typography>
              </Box>
            ))}
          </Box>

          {/* ── PANEL 2: Conjunction (0°) + polarity ── */}
          <Box sx={{ ...panelBorder, pt: 1 }}>
            {/* Header row 1: select-all checkbox */}
            <Box sx={{ height: ROW_H, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <GreenCb checked={colHeaders[0]} onChange={() => toggleColHeader(0)} />
            </Box>
            {/* Header row 2: spacer to align with polarity row in Panel 3 */}
            <Box sx={{ height: ROW_H }} />
            {/* Header row 3: labels */}
            <Box sx={{ height: ROW_H, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography fontWeight={700} fontSize={12} lineHeight={1.1}>+ve or</Typography>
                <Typography fontWeight={700} fontSize={12} lineHeight={1.1}>-ve</Typography>
              </Box>
              <Typography fontWeight={700} fontSize={14}>0</Typography>
            </Box>
            {/* Data rows */}
            {PLANETS.map(planet => (
              <Box key={planet} sx={{ height: ROW_H, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <GreenCb checked={planetAspects[planet][0]} onChange={() => toggleAspect(planet, 0)} />
                <PolarityBtn
                  value={conjPolarity[planet]}
                  onChange={v => { goCustom(); setConjPolarity(prev => ({ ...prev, [planet]: v })); }}
                  disabled={!planetAspects[planet][0]}
                />
              </Box>
            ))}
            {/* Conjunction weight */}
            <Box sx={{ height: ROW_H, display: 'flex', alignItems: 'center', justifyContent: 'center', borderTop: '1px solid #ddd', mt: 0.5, pt: 0.5 }}>
              <WeightBox
                value={aspectWeights[0]}
                onChange={v => { goCustom(); setAspectWeights(prev => ({ ...prev, 0: v })); }}
              />
            </Box>
          </Box>

          {/* ── PANEL 3: Aspects 30–180 ── */}
          <Box sx={{ ...panelBorder, pt: 1 }}>
            {/* Header row 1: select-all checkboxes */}
            <Box sx={{ height: ROW_H, display: 'flex', alignItems: 'center' }}>
              {OTHER_ASPECTS.map((_, i) => (
                <Box key={i} sx={{ width: CELL_W, display: 'flex', justifyContent: 'center' }}>
                  <GreenCb checked={colHeaders[i + 1]} onChange={() => toggleColHeader(i + 1)} />
                </Box>
              ))}
            </Box>
            {/* Aspect-level polarity row (above angle labels) */}
            <Box sx={{ height: ROW_H, display: 'flex', alignItems: 'center' }}>
              {OTHER_ASPECTS.map(angle => (
                <Box key={angle} sx={{ width: CELL_W, display: 'flex', justifyContent: 'center' }}>
                  <PolarityBtn
                    value={aspectPolarity[angle] || '+'}
                    onChange={v => { goCustom(); setAspectPolarity(prev => ({ ...prev, [angle]: v })); }}
                  />
                </Box>
              ))}
            </Box>
            {/* Header row 2: angle labels */}
            <Box sx={{ height: ROW_H, display: 'flex', alignItems: 'center' }}>
              {OTHER_ASPECTS.map(a => <ColLabel key={a} label={a} />)}
            </Box>
            {/* Data rows */}
            {PLANETS.map(planet => (
              <Box key={planet} sx={{ height: ROW_H, display: 'flex', alignItems: 'center' }}>
                {OTHER_ASPECTS.map((_, i) => (
                  <Box key={i} sx={{ width: CELL_W, display: 'flex', justifyContent: 'center' }}>
                    <GreenCb checked={planetAspects[planet][i + 1]} onChange={() => toggleAspect(planet, i + 1)} />
                  </Box>
                ))}
              </Box>
            ))}
            {/* Aspect weight row */}
            <Box sx={{ height: ROW_H, display: 'flex', alignItems: 'center', mt: 0.5 }}>
              {OTHER_ASPECTS.map(angle => (
                <Box key={angle} sx={{ width: CELL_W, display: 'flex', justifyContent: 'center' }}>
                  <WeightBox
                    value={aspectWeights[angle]}
                    onChange={v => { goCustom(); setAspectWeights(prev => ({ ...prev, [angle]: v })); }}
                  />
                </Box>
              ))}
            </Box>
          </Box>

          {/* ── PANEL 4: Declination ── */}
          <Box sx={{ ...panelBorder, pt: 1 }}>
            {/* Header row 1: select-all */}
            <Box sx={{ height: ROW_H, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <GreenCb checked={declHeader} onChange={toggleDeclHeader} />
            </Box>
            {/* Header row 2: spacer to align with polarity/weight rows in other panels */}
            <Box sx={{ height: ROW_H }} />
            {/* Header row 3: label */}
            <Box sx={{ height: ROW_H, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography fontWeight={700} fontSize={13}>Declination</Typography>
            </Box>
            {/* Data rows */}
            {PLANETS.map(planet => (
              <Box key={planet} sx={{ height: ROW_H, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <GreenCb checked={planetDecl[planet]} onChange={() => toggleDecl(planet)} />
              </Box>
            ))}
          </Box>

          {/* ── PANEL 5: Presets + Legend ── */}
          <Box sx={{ minWidth: 190, pt: `${ROW_H * 2}px` }}>
            <RadioGroup value={preset} onChange={e => applyPreset(e.target.value)}>
              {[['classic','Classic'],['all','All'],['vedic_plus','Vedic+']].map(([val, label]) => (
                <FormControlLabel key={val} value={val}
                  control={<Radio size="small" sx={{ py: 0.3 }} />}
                  label={<Typography fontSize={15}>{label}</Typography>}
                />
              ))}
            </RadioGroup>

            {/* Custom section in its own bordered box */}
            <Paper variant="outlined" sx={{ mt: 1, p: 1, borderRadius: 1 }}>
              <RadioGroup value={preset} onChange={e => applyPreset(e.target.value)}>
                <FormControlLabel value="custom"
                  control={<Radio size="small" sx={{ py: 0.3 }} />}
                  label={<Typography fontSize={15}>Custom</Typography>}
                />
              </RadioGroup>
              {LEGEND_ITEMS.map(item => (
                <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
                  <GreenCb checked={legend[item.key]} onChange={() => toggleLegend(item.key)} />
                  <Typography fontSize={14}>{item.label}</Typography>
                </Box>
              ))}
            </Paper>

            <Button
              variant="outlined"
              size="small"
              onClick={handleDeselect}
              sx={{ mt: 1, textTransform: 'none', minWidth: 92, alignSelf: 'flex-start' }}
            >
              Deselect All
            </Button>
          </Box>

          {/* ── PANEL 6: Weights + Line Style + Color Picker + Tolerance ── */}
          <Box sx={{ ...panelBorder, minWidth: 210 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={600} fontSize={13} sx={{ mb: 0.5 }}>Long weight</Typography>
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  value={longWeight}
                  onChange={e => { goCustom(); setLongWeight(Number(e.target.value)); }}
                  inputProps={{ min: 0, step: 0.5 }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={600} fontSize={13} sx={{ mb: 0.5 }}>Medium weight</Typography>
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  value={mediumWeight}
                  onChange={e => { goCustom(); setMediumWeight(Number(e.target.value)); }}
                  inputProps={{ min: 0, step: 0.5 }}
                />
              </Box>
            </Box>

            <Typography fontWeight={600} fontSize={13} sx={{ mb: 0.5 }}>Smoothing (days)</Typography>
            <TextField
              type="number"
              size="small"
              fullWidth
              value={classicSmoothingDays ?? ''}
              onChange={e => {
                const raw = e.target.value;
                if (raw === '') {
                  setClassicSmoothingDays(null);
                  return;
                }
                const next = Number(raw);
                setClassicSmoothingDays(Number.isFinite(next) ? Math.max(1, Math.round(next)) : null);
              }}
              inputProps={{ min: 1, step: 1 }}
              sx={{ mb: 1.5 }}
            />

            <Typography fontWeight={600} fontSize={14} sx={{ mb: 0.5 }}>Line type</Typography>
            <Select
              value={lineType}
              onChange={e => setLineType(e.target.value)}
              size="small"
              fullWidth
              sx={{ mb: 1.5 }}
            >
              <MenuItem value="solid">Solid</MenuItem>
              <MenuItem value="dotted">Dotted</MenuItem>
              <MenuItem value="dashed">Dashed</MenuItem>
            </Select>

            <Typography fontWeight={600} fontSize={14} sx={{ mb: 0.5 }}>Line thickness</Typography>
            <Select
              value={lineThickness}
              onChange={e => setLineThickness(e.target.value)}
              size="small"
              fullWidth
              sx={{ mb: 1.5 }}
            >
              <MenuItem value={1}>1</MenuItem>
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={3}>3</MenuItem>
            </Select>

            <Typography fontWeight={600} fontSize={14} sx={{ mb: 0.5 }}>Select Graph color</Typography>
            <SketchPicker
              color={lineColor}
              onChange={c => setLineColor(c.hex)}
              width="200px"
              styles={{ default: { picker: { boxShadow: 'none', border: '1px solid #ddd', borderRadius: 4 } } }}
            />

            <Typography fontWeight={600} fontSize={13} sx={{ mt: 1.5, mb: 0.5 }}>Tolerance (days)</Typography>
            <TextField
              type="number"
              size="small"
              fullWidth
              value={toleranceDays}
              onChange={e => setToleranceDays(Math.max(1, Math.round(Number(e.target.value) || 5)))}
              inputProps={{ min: 1, max: 30, step: 1 }}
            />
          </Box>

          {/* ── PANEL 7: Commentary ── */}
          <Box sx={{ ...panelBorder, minWidth: 280, maxWidth: 340, display: 'flex', flexDirection: 'column' }}>
            <Typography fontWeight={700} fontSize={16} sx={{ mb: 1 }}>Commentary:</Typography>
            <TextField
              multiline
              minRows={18}
              maxRows={24}
              fullWidth
              value={commentary || ''}
              InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12, lineHeight: 1.4 } }}
              variant="outlined"
              sx={{ flexGrow: 1 }}
            />
          </Box>

        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
          {/* Left: CLOSE */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
            <Button onClick={onClose} variant="outlined"
              sx={{ fontSize: 14, minWidth: 90, borderColor: '#9e9e9e', color: '#333',
                '&:hover': { borderColor: '#757575', backgroundColor: '#f5f5f5' } }}>
              CLOSE
            </Button>
          </Box>
          {/* Center: EXECUTE */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <Button onClick={handleExecute} variant="contained"
              sx={{ fontSize: 14, minWidth: 120, backgroundColor: '#1565c0', '&:hover': { backgroundColor: '#0d47a1' } }}>
              EXECUTE
            </Button>
          </Box>
          {/* Right: Study controls */}
          <Box sx={{ flex: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5, alignItems: 'center' }}>
            <TextField
              type="date"
              size="small"
              label="Study start date"
              value={studyStartDate || defaultStudyDate}
              onChange={e => setStudyStartDate(e.target.value)}
              inputProps={{ min: minStudyDate, max: maxStudyDate }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 170 }}
            />
            <Button onClick={() => { if (onExecuteAndStudy) onExecuteAndStudy({
                planetEnabled, planetAspects, conjPolarity, aspectPolarity,
                planetWeights, aspectWeights, planetDecl, legend, preset,
                longWeight, mediumWeight, classicSmoothingDays,
                lineType, lineThickness, lineColor, toleranceDays,
                studyStartDate: studyStartDate || defaultStudyDate,
              }); }} variant="contained"
              sx={{ fontSize: 14, minWidth: 160, backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#1b5e20' } }}>
              EXECUTE &amp; STUDY
            </Button>
            <TextField
              size="small"
              label="Delay (sec)"
              value={animateDelay}
              onChange={e => {
                const v = e.target.value.replace(/[^0-9.]/g, '');
                setAnimateDelay(v);
              }}
              inputProps={{ min: 0.5, max: 30, step: 0.5 }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 90 }}
            />
            <Button onClick={() => { if (onAnimate) onAnimate({
                planetEnabled, planetAspects, conjPolarity, aspectPolarity,
                planetWeights, aspectWeights, planetDecl, legend, preset,
                longWeight, mediumWeight, classicSmoothingDays,
                lineType, lineThickness, lineColor, toleranceDays,
                studyStartDate: studyStartDate || defaultStudyDate,
                animateDelaySec: Number(animateDelay) || 1,
              }); }} variant="contained"
              sx={{ fontSize: 14, minWidth: 110, backgroundColor: '#e65100', '&:hover': { backgroundColor: '#bf360c' } }}>
              ANIMATE
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

AstroGraphDialog.propTypes = {
  open:              PropTypes.bool.isRequired,
  onClose:           PropTypes.func.isRequired,
  onExecute:         PropTypes.func,
  onExecuteAndStudy: PropTypes.func,
  onAnimate:         PropTypes.func,
  commentary:        PropTypes.string,
  barData:           PropTypes.object,
};

export default AstroGraphDialog;


